import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { withSetorScope } from '../../utils/dataIsolation';
import { useAuth } from '../../contexts/AuthContext';
import { useModuloFiscalData } from './useModuloFiscalData';
import { parseResponsaveisIds } from '../contratos/utils/processoEtapaPermissao';
import './moduloFiscal.css';

const TIPOS_PROCESSO = [
  {
    id: 'contratacao',
    titulo: 'Contratação',
    descricao: 'CI → TR → autorizações → edital → propostas → minuta → Prestadores',
    ativo: true,
  },
  {
    id: 'aditivo',
    titulo: 'Aditivo',
    descricao: 'Em breve — mesmo padrão de fluxo da Configuração',
    ativo: false,
  },
  {
    id: 'apostilamento',
    titulo: 'Apostilamento',
    descricao: 'Em breve',
    ativo: false,
  },
];

/**
 * Hub Processo Administrativo (módulo fiscal):
 * escolhe o tipo de processo e lista tramitações da unidade/setor.
 * Motor = fluxos de Contratos → Configuração.
 */
export default function ModuloFiscalProcessoAdministrativoPage() {
  const { fiscalId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const { loading: loadingBase, error: errorBase, fiscal } = useModuloFiscalData(fiscalId);

  const [loading, setLoading] = useState(true);
  const [processos, setProcessos] = useState([]);
  const [fluxoNome, setFluxoNome] = useState('');
  const [msg, setMsg] = useState('');

  const basePath = `/modulos/fiscal/${fiscalId}/processo-administrativo`;

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      let urlProc = withUnidade('/api/contratos/processos?tipoFluxo=CONTRATACAO', unidadeSelecionada);
      urlProc = withSetorScope(urlProc, { user, rbacRole, fiscalSetor });
      const urlFluxos = withUnidade('/api/contratos/fluxos', unidadeSelecionada);

      const [resP, resF] = await Promise.all([apiFetch(urlProc), apiFetch(urlFluxos)]);
      const dataP = await resP.json().catch(() => ([]));
      const dataF = await resF.json().catch(() => ([]));

      if (!resP.ok) throw new Error(dataP?.error || 'Erro ao listar processos');

      const lista = Array.isArray(dataP) ? dataP : dataP?.processos || dataP?.data || [];
      const uid = Number(user?.id);
      const email = String(user?.email || '')
        .trim()
        .toLowerCase();
      const nome = String(user?.nome || '').trim().toLowerCase();

      // Fiscal vê: processos em que é responsável da etapa OU criou OU (lista da unidade se não for setorial estrito)
      const filtrados = lista.filter((p) => {
        const ids = parseResponsaveisIds(
          p.etapaAtual?.responsaveisIds || p.etapaResponsaveisIds || p.etapa_responsaveis_ids
        );
        const isResp = uid && ids.includes(uid);
        const criador = String(p.criadoPor || p.criado_por || '')
          .trim()
          .toLowerCase();
        const isCriador =
          (email && criador.includes(email)) || (nome && criador && criador === nome);
        return isResp || isCriador || ids.length === 0;
      });

      setProcessos(filtrados);

      const fluxos = Array.isArray(dataF) ? dataF : dataF?.fluxos || [];
      const ativo = fluxos.find((f) => f.ativo === 1 || f.ativo === true) || fluxos[0];
      setFluxoNome(ativo?.nome || 'Fluxo de contratação (Configuração)');
    } catch (e) {
      console.error(e);
      setMsg(e.message || 'Erro ao carregar');
      setProcessos([]);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    if (!loadingBase && fiscal) load();
  }, [loadingBase, fiscal, load]);

  const statusClass = useMemo(
    () => (s) => {
      const u = String(s || '').toUpperCase();
      if (u.includes('CONCLUI') || u.includes('FINAL')) return 'mf-badge--ok';
      if (u.includes('REJEIT')) return 'mf-badge--warn';
      return 'mf-badge--info';
    },
    []
  );

  if (loadingBase) {
    return (
      <div className="mf-page" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <CircularProgress sx={{ color: '#5ce1c5' }} />
      </div>
    );
  }

  if (errorBase || !fiscal) {
    return (
      <div className="mf-page">
        <p className="mf-page__meta">{errorBase || 'Fiscal não encontrado.'}</p>
      </div>
    );
  }

  return (
    <div className="mf-page">
      <div className="mf-page__header">
        <div>
          <h1 className="mf-page__title">Processo Administrativo</h1>
          <p className="mf-page__meta">
            {fiscal.nome} · {fiscal.setor} · Trilha moldada por{' '}
            <strong style={{ color: '#5ce1c5' }}>{fluxoNome}</strong>
          </p>
        </div>
        <button type="button" className="mf-page__back" onClick={() => load()} disabled={loading}>
          Atualizar
        </button>
      </div>

      {msg ? <p className="mf-alert mf-alert--error">{msg}</p> : null}

      <h2 className="mf-section-title">Abrir processo</h2>
      <div className="mf-pa-tipos">
        {TIPOS_PROCESSO.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`mf-pa-tipo${t.ativo ? '' : ' mf-pa-tipo--disabled'}`}
            disabled={!t.ativo}
            onClick={() => t.ativo && navigate(`${basePath}/contratacao`)}
          >
            <span className="mf-pa-tipo__icon">
              {t.ativo ? <AssignmentIcon fontSize="inherit" /> : <LockIcon fontSize="inherit" />}
            </span>
            <span className="mf-pa-tipo__body">
              <strong>{t.titulo}</strong>
              <span>{t.descricao}</span>
            </span>
            {t.ativo ? (
              <span className="mf-pa-tipo__cta">
                <AddIcon fontSize="small" /> Iniciar
              </span>
            ) : (
              <span className="mf-pa-tipo__cta mf-pa-tipo__cta--muted">Em breve</span>
            )}
          </button>
        ))}
      </div>

      <h2 className="mf-section-title">Meus processos / em andamento</h2>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={28} sx={{ color: '#5ce1c5' }} />
        </div>
      ) : processos.length === 0 ? (
        <p className="mf-page__meta">Nenhum processo de contratação nesta visão.</p>
      ) : (
        <div className="mf-table-wrap">
          <table className="mf-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Título</th>
                <th>Etapa</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {processos.map((p) => (
                <tr key={p.id}>
                  <td>{p.codigo}</td>
                  <td>{p.titulo}</td>
                  <td>{p.etapaAtual?.nome || p.etapaNome || p.etapa_nome || '—'}</td>
                  <td>
                    <span className={`mf-badge ${statusClass(p.status)}`}>{p.status || '—'}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="mf-btn mf-btn--ghost"
                      onClick={() => navigate(`${basePath}/${p.id}`)}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mf-page__meta" style={{ marginTop: '1.5rem' }}>
        Ao finalizar a trilha com minuta, o contrato é gerado e alimenta{' '}
        <strong>Contratos → Prestadores</strong>.
      </p>
    </div>
  );
}
